import React from 'react';
import cn from 'classnames';
import ActionBarStrip from '../../_partials/ActionBarStrip';
import FavouriteButton from '../../buttons/FavouriteButtonWithPopover';
import CommentButton from '../../buttons/CommentButton';
import AddToGroupButton from '../../buttons/AddToGroupButton/AddToGroupButtonWithPopover';
import MoreButton from '../../buttons/MoreButton';
import DeleteButton from '../../buttons/DeleteButton';
import EditButton from '../../buttons/EditButton';
import AwardBadgeButton from '../../buttons/AwardBadgeButton';
import AdoptableSeller from '../../buttons/AdoptableSeller';
import AddToPrivateCollectionButtonWithPopover from '../../buttons/AddToPrivateCollectionButtonWithPopover';
import AdoptableBuyer from '../../buttons/AdoptableBuyer';
import OrderCommissionButton from '../../buttons/OrderCommissionButton';
import FreeDownloadButton from '../../buttons/DownloadButton/FreeDownloadButton';
import LoginToDownloadButton from '../../buttons/LoginToDownloadButton';
import SellYourArtLink from '../../buttons/SellYourArtLink';
import CommissionStatusButton from '../../buttons/CommissionStatusButton';
import { StandardDeviationElementPlacement } from '../../ElementPlacementContext/variants/standard/types';
import { StandardDeviationElementPlacementContext } from '../../ElementPlacementContext/variants/standard/context';
import { useElementPlacementForArea } from '../../ElementPlacementContext/utils';
import BoostButton from '../../buttons/BoostButton';
import { ActionBarProps as Props } from '../../types';

import s from './MobileActionBar.scss';

export const MobileActionBar: React.FC<Props> = ({
  deviation,
  isOwner,
  className,
}) => {
  const primaryBarElementPlacement = useElementPlacementForArea(
    StandardDeviationElementPlacement.PrimaryBar,
    StandardDeviationElementPlacementContext
  );
  const secondaryBarElementPlacement = useElementPlacementForArea(
    StandardDeviationElementPlacement.SecondaryBar,
    StandardDeviationElementPlacementContext
  );

  return (
    <ActionBarStrip className={cn(s['root'], className)}>
      <div className={s['icons-wrapper']}>
        <div className={s['left-block']}>
          {primaryBarElementPlacement.favourite && (
            <span className={s['icon']}>
              <FavouriteButton deviation={deviation} actionBarType="primary" />
            </span>
          )}
          {primaryBarElementPlacement.comment && (
            <span className={s['icon']}>
              <CommentButton deviation={deviation} isOwner={isOwner} />
            </span>
          )}
          {primaryBarElementPlacement.addToGroup &&
            !primaryBarElementPlacement.meta.hasPrimaryMoreActionsMenu && (
              <span className={s['icon']}>
                <AddToGroupButton deviation={deviation} />
              </span>
            )}
        </div>

        <div className={s['right-block']}>
          {primaryBarElementPlacement.orderCommission && (
            <span className={s['icon']}>
              <OrderCommissionButton deviation={deviation} />
            </span>
          )}
          {primaryBarElementPlacement.privateCollection && (
            <span className={s['icon']}>
              <AddToPrivateCollectionButtonWithPopover deviation={deviation} />
            </span>
          )}
          {
            /**
             * The "add to group" button can be in two different spots on the primary bar depending on
             * if the more menu is present.
             *
             * NOTE: We shouldn't use this method to swap controls between bars or the more menu. That's what
             * our {@link StandardDeviationElementPlacementMap} is for. However, this is fine to check against if we're
             * only moving it around within the domain of the indicated area. (e.g. if {@link StandardDeviationElementPlacementMap}
             * has a control appearing in {@link StandardDeviationElementPlacement.PrimaryBar}, it's fine to move the
             * control around inside the primary bar, but it's not fine to have it jump to the secondary bar. Alter the
             * rules inside {@link StandardDeviationElementPlacementMap} instead if you need that behaviour)
             */
            primaryBarElementPlacement.addToGroup &&
              primaryBarElementPlacement.meta.hasPrimaryMoreActionsMenu && (
                <span className={s['icon']}>
                  <AddToGroupButton deviation={deviation} />
                </span>
              )
          }
          {primaryBarElementPlacement.awardBadge && (
            <span className={s['icon']}>
              <AwardBadgeButton deviation={deviation} />
            </span>
          )}
          {primaryBarElementPlacement.freeDownload && (
            <span className={s['icon']}>
              <FreeDownloadButton deviation={deviation} />
            </span>
          )}
          {primaryBarElementPlacement.boostDeviation && (
            <span className={s['icon']}>
              <BoostButton deviation={deviation} />
            </span>
          )}
          {primaryBarElementPlacement.meta.hasPrimaryMoreActionsMenu && (
            <span className={s['icon']}>
              <MoreButton deviation={deviation} />
            </span>
          )}
          {primaryBarElementPlacement.deleteDeviation && (
            <span className={s['icon']}>
              <DeleteButton deviation={deviation} />
            </span>
          )}
          {primaryBarElementPlacement.editDeviation && (
            <span className={s['icon']}>
              <EditButton deviation={deviation} />
            </span>
          )}
          {primaryBarElementPlacement.commissionStatus && (
            <span className={s['icon']}>
              <CommissionStatusButton deviation={deviation} />
            </span>
          )}
        </div>
      </div>

      <div className={s['download-button-row']}>
        {secondaryBarElementPlacement.sellDeviation && (
          <SellYourArtLink deviation={deviation} />
        )}

        {secondaryBarElementPlacement.loginToDownload && (
          <LoginToDownloadButton
            className={s['full-width']}
            deviation={deviation}
          />
        )}

        {secondaryBarElementPlacement.elementGroups.adoptableSeller
          .groupPlacement && <AdoptableSeller deviation={deviation} />}

        {secondaryBarElementPlacement.elementGroups.adoptableBuyer
          .groupPlacement && <AdoptableBuyer deviation={deviation} />}
      </div>
    </ActionBarStrip>
  );
};
MobileActionBar.displayName = 'MobileActionBar';

export default MobileActionBar;
